<template>
  <section class="webVoiceVisualSettings">
    <div class="flex items-start justify-between">
      <div class="pr-6">
        <h1 class="text-2xl font-medium">{{ t('aiChatAgentSetup.webVoiceSettings.title', 'Web Voice Settings') }}</h1>
        <p class="mt-2 text-sm text-base-content/60">{{ t('aiChatAgentSetup.webVoiceSettings.description', 'Let the voice agent present visual content while it speaks.') }}</p>
      </div>
      <label class="flex items-center cursor-pointer">
        <span class="text-sm font-medium mr-3">{{ t('aiChatAgentSetup.webVoiceSettings.enableCanvas', 'Enable visual canvas') }}</span>
        <input type="checkbox" class="toggle toggle-accent" :checked="settings.enabled === true" @change="setEnabled($event.target.checked)" />
      </label>
    </div>

    <div v-if="settings.enabled === true" class="mt-6">
      <div class="rounded-box border border-base-300 bg-base-200/40 p-5">
        <div class="flex items-center justify-between">
          <div>
            <h2 class="font-semibold">{{ t('aiChatAgentSetup.webVoiceSettings.uploadTitle', 'Visual and button library') }}</h2>
            <p class="mt-1 text-xs text-base-content/60">{{ t('aiChatAgentSetup.webVoiceSettings.uploadDescription', 'Upload media files or add buttons. Add a clear instruction for when each item should appear.') }}</p>
          </div>
          <div class="dropdown dropdown-end ml-4" :class="{ 'dropdown-open': isAddDropdownOpen === true }">
            <button type="button" class="btn" @click.stop="toggleAddDropdown">
              {{ t('aiChatAgentSetup.webVoiceSettings.addItem', 'Add Item') }}
              <Icon icon="heroicons:chevron-down" class="w-4 h-4 ml-2" />
            </button>
            <ul v-if="isAddDropdownOpen === true" tabindex="0" class="dropdown-content z-[60] menu p-2 shadow bg-base-100 rounded-box w-60">
              <li>
                <label class="cursor-pointer">
                  <input ref="fileInput" type="file" class="hidden" multiple accept="image/png,image/jpeg,image/gif,image/webp,image/avif,video/mp4,video/webm,video/ogg" @change="handleFiles" />
                  {{ t('aiChatAgentSetup.webVoiceSettings.uploadMediaOption', 'Upload media files') }}
                </label>
              </li>
              <li><button type="button" @click="addButtonItem('purchase')">{{ t('aiChatAgentSetup.webVoiceSettings.addPurchaseButton', 'Add purchase button') }}</button></li>
              <li><button type="button" @click="addButtonItem('regular')">{{ t('aiChatAgentSetup.webVoiceSettings.addRegularButton', 'Add regular button') }}</button></li>
            </ul>
          </div>
        </div>
        <div class="mt-3 text-xs text-base-content/50">
          {{ t('aiChatAgentSetup.webVoiceSettings.sizeLimit', 'Maximum 12 MB per item and 15 MB total.') }}
        </div>
      </div>

      <div v-if="settings.items.length > 0" class="mt-5 overflow-x-auto rounded-box border border-base-300">
        <table class="table">
          <thead>
            <tr>
              <th>{{ t('aiChatAgentSetup.webVoiceSettings.type', 'Type') }}</th>
              <th>{{ t('aiChatAgentSetup.webVoiceSettings.item', 'Item') }}</th>
              <th class="min-w-[360px]">{{ t('aiChatAgentSetup.webVoiceSettings.timingPrompt', 'When should the AI show this?') }}</th>
              <th class="min-w-[220px]">{{ t('aiChatAgentSetup.webVoiceSettings.buttonLabel', 'Button label') }}</th>
              <th class="min-w-[260px]">{{ t('aiChatAgentSetup.webVoiceSettings.buttonLink', 'Button link') }}</th>
              <th class="w-24"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in settings.items" :key="item.id">
              <td>
                <span class="badge badge-outline">{{ getItemTypeLabel(item) }}</span>
              </td>
              <td>
                <div v-if="isMediaItem(item) === true" class="flex items-center">
                  <div class="h-20 w-28 overflow-hidden rounded-xl bg-base-200 flex items-center justify-center">
                    <img v-if="isImage(item) === true" :src="item.dataUrl" :alt="item.name" class="h-full w-full object-cover" />
                    <video v-else :src="item.dataUrl" class="h-full w-full object-cover" muted loop controls preload="metadata"></video>
                  </div>
                  <div class="ml-3">
                    <div class="max-w-48 truncate font-medium" :title="item.name">{{ item.name }}</div>
                    <div class="mt-1 text-xs text-base-content/50">{{ formatBytes(item.size) }}</div>
                  </div>
                </div>
                <div v-else>
                  <input
                    type="text"
                    class="input input-bordered input-sm w-full"
                    maxlength="120"
                    :value="typeof item.name === 'string' ? item.name : ''"
                    :placeholder="getButtonNamePlaceholder(item)"
                    @input="updateItemField(item.id, 'name', $event.target.value)"
                  />
                  <button type="button" class="btn btn-sm mt-2" disabled>
                    {{ resolveButtonPreviewLabel(item) }}
                  </button>
                </div>
              </td>
              <td>
                <textarea
                  class="textarea textarea-bordered w-full min-h-24"
                  maxlength="1000"
                  :value="item.prompt"
                  :placeholder="t('aiChatAgentSetup.webVoiceSettings.promptPlaceholder', 'Example: Show this product demo when the visitor asks how onboarding works.')"
                  @input="updatePrompt(item.id, $event.target.value)"
                ></textarea>
                <div class="mt-1 text-right text-xs text-base-content/40">{{ typeof item.prompt === 'string' ? item.prompt.length : 0 }}/1000</div>
              </td>
              <td>
                <input
                  type="text"
                  class="input input-bordered input-sm w-full"
                  maxlength="80"
                  :value="typeof item.actionLabel === 'string' ? item.actionLabel : ''"
                  :placeholder="getButtonLabelPlaceholder(item)"
                  @input="updateActionLabel(item.id, $event.target.value)"
                />
              </td>
              <td>
                <input
                  type="url"
                  class="input input-bordered input-sm w-full"
                  maxlength="1000"
                  :value="typeof item.actionUrl === 'string' ? item.actionUrl : ''"
                  placeholder="https://example.com"
                  @input="updateActionUrl(item.id, $event.target.value)"
                />
              </td>
              <td class="text-right">
                <button type="button" class="btn btn-error btn-sm" @click="requestDelete(item)">
                  {{ t('aiChatAgentSetup.buttons.delete', 'Delete') }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="mt-5 rounded-box border border-dashed border-base-300 p-10 text-center">
        <Icon icon="tabler:photo-video" class="mx-auto h-10 w-10 text-base-content/30" />
        <p class="mt-3 font-medium">{{ t('aiChatAgentSetup.webVoiceSettings.emptyTitle', 'No visuals uploaded yet') }}</p>
        <p class="mt-1 text-sm text-base-content/50">{{ t('aiChatAgentSetup.webVoiceSettings.emptyDescription', 'Uploaded items will be preloaded by the web voice widget.') }}</p>
      </div>
    </div>

    <dialog class="modal" :class="deleteItem !== null ? 'modal-open' : ''">
      <div class="modal-box">
        <h3 class="text-lg font-bold">{{ t('aiChatAgentSetup.webVoiceSettings.deleteTitle', 'Delete visual?') }}</h3>
        <p class="py-4">{{ t('aiChatAgentSetup.webVoiceSettings.deleteDescription', 'The AI will no longer be able to display this item.') }}</p>
        <div class="modal-action">
          <button type="button" class="btn" @click="deleteItem = null">{{ t('aiChatAgentSetup.buttons.cancel', 'Cancel') }}</button>
          <button type="button" class="btn btn-error" @click="confirmDelete">{{ t('aiChatAgentSetup.buttons.delete', 'Delete') }}</button>
        </div>
      </div>
      <form method="dialog" class="modal-backdrop">
        <button type="button" @click="deleteItem = null">close</button>
      </form>
    </dialog>
  </section>
</template>

<script setup>
import { computed, ref } from 'vue';
import { Icon } from '@iconify/vue/dist/offline';
import { useToast } from 'vue-toastification';
import { useI18n } from '@/i18n';

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({ enabled: false, items: [] })
  }
});

const emit = defineEmits(['update:modelValue']);
const toast = useToast();
const { t } = useI18n();
const fileInput = ref(null);
const deleteItem = ref(null);
const isAddDropdownOpen = ref(false);
const maxItemBytes = 12 * 1024 * 1024;
const maxTotalBytes = 15 * 1024 * 1024;
const maxItems = 30;

const settings = computed(() => {
  const value = props.modelValue !== null && props.modelValue !== undefined && typeof props.modelValue === 'object' ? props.modelValue : {};
  return {
    enabled: value.enabled === true,
    items: Array.isArray(value.items) === true ? value.items : []
  };
});

function createId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function emitSettings(enabledParam, itemsParam) {
  emit('update:modelValue', {
    enabled: enabledParam === true,
    items: Array.isArray(itemsParam) === true ? itemsParam : []
  });
}

function setEnabled(enabledParam) {
  emitSettings(enabledParam, settings.value.items);
}

function toggleAddDropdown() {
  isAddDropdownOpen.value = isAddDropdownOpen.value !== true;
}

function isSupportedFile(fileParam) {
  const type = fileParam !== undefined && fileParam !== null && typeof fileParam.type === 'string' ? fileParam.type.toLowerCase() : '';
  const imageTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/avif'];
  const videoTypes = ['video/mp4', 'video/webm', 'video/ogg'];
  return imageTypes.includes(type) === true || videoTypes.includes(type) === true;
}

function isImage(itemParam) {
  return itemParam !== undefined && itemParam !== null && typeof itemParam.type === 'string' && itemParam.type.startsWith('image/') === true;
}

function isMediaItem(itemParam) {
  const item = itemParam !== undefined && itemParam !== null && typeof itemParam === 'object' ? itemParam : {};
  const type = typeof item.type === 'string' ? item.type.toLowerCase() : '';
  return type.startsWith('image/') === true || type.startsWith('video/') === true;
}

function getItemActionType(itemParam) {
  const item = itemParam !== undefined && itemParam !== null && typeof itemParam === 'object' ? itemParam : {};
  const actionType = typeof item.actionType === 'string' ? item.actionType : '';
  if (actionType === 'purchase') {
    return 'purchase';
  }
  if (actionType === 'regular') {
    return 'regular';
  }
  const type = typeof item.type === 'string' ? item.type.toLowerCase() : '';
  if (type === 'action/purchase') {
    return 'purchase';
  }
  if (type === 'action/button') {
    return 'regular';
  }
  return '';
}

function getItemTypeLabel(itemParam) {
  if (isMediaItem(itemParam) === true) {
    return t('aiChatAgentSetup.webVoiceSettings.mediaType', 'Media');
  }
  if (getItemActionType(itemParam) === 'purchase') {
    return t('aiChatAgentSetup.webVoiceSettings.purchaseButton', 'Purchase button');
  }
  return t('aiChatAgentSetup.webVoiceSettings.regularButton', 'Regular button');
}

function getButtonNamePlaceholder(itemParam) {
  if (getItemActionType(itemParam) === 'purchase') {
    return t('aiChatAgentSetup.webVoiceSettings.purchaseButtonNamePlaceholder', 'Purchase action');
  }
  return t('aiChatAgentSetup.webVoiceSettings.regularButtonNamePlaceholder', 'Regular action');
}

function getButtonLabelPlaceholder(itemParam) {
  if (getItemActionType(itemParam) === 'purchase') {
    return t('aiChatAgentSetup.webVoiceSettings.purchaseLabelPlaceholder', 'Buy now');
  }
  return t('aiChatAgentSetup.webVoiceSettings.regularLabelPlaceholder', 'Learn more');
}

function resolveButtonPreviewLabel(itemParam) {
  const item = itemParam !== undefined && itemParam !== null && typeof itemParam === 'object' ? itemParam : {};
  const actionLabel = typeof item.actionLabel === 'string' && item.actionLabel.trim() !== '' ? item.actionLabel.trim() : '';
  if (actionLabel !== '') {
    return actionLabel;
  }
  return getButtonLabelPlaceholder(item);
}

function readFile(fileParam) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(new Error('fileReadFailed'));
    reader.readAsDataURL(fileParam);
  });
}

async function handleFiles(eventParam) {
  const eventTarget = eventParam !== undefined && eventParam !== null && eventParam.target !== undefined ? eventParam.target : {};
  const files = Array.from(eventTarget.files !== undefined ? eventTarget.files : []);
  let items = settings.value.items.slice();
  let totalBytes = items.reduce((sum, item) => sum + (Number.isFinite(Number(item.size)) === true ? Number(item.size) : 0), 0);
  isAddDropdownOpen.value = false;
  for (let index = 0; index < files.length; index = index + 1) {
    const file = files[index];
    if (items.length >= maxItems) {
      toast.error(t('aiChatAgentSetup.webVoiceSettings.itemLimit', 'The visual library can contain up to 30 items.'));
      break;
    }
    if (isSupportedFile(file) !== true) {
      toast.error(`${file.name}: ${t('aiChatAgentSetup.webVoiceSettings.unsupportedFile', 'Unsupported file type.')}`);
      continue;
    }
    if (file.size > maxItemBytes) {
      toast.error(`${file.name}: ${t('aiChatAgentSetup.webVoiceSettings.itemTooLarge', 'The file is larger than 12 MB.')}`);
      continue;
    }
    if (totalBytes + file.size > maxTotalBytes) {
      toast.error(t('aiChatAgentSetup.webVoiceSettings.totalTooLarge', 'The visual library cannot exceed 15 MB.'));
      break;
    }
    try {
      const dataUrl = await readFile(file);
      if (dataUrl === '') {
        throw new Error('fileReadFailed');
      }
      items.push({
        id: createId(),
        itemKind: 'media',
        actionType: '',
        name: file.name.replace(/_/g, ' '),
        type: file.type,
        size: file.size,
        dataUrl,
        prompt: '',
        actionLabel: '',
        actionUrl: ''
      });
      totalBytes = totalBytes + file.size;
    } catch (error) {
      toast.error(`${file.name}: ${t('aiChatAgentSetup.webVoiceSettings.readFailed', 'Could not read this file.')}`);
    }
  }
  emitSettings(true, items);
  if (fileInput.value !== null) {
    fileInput.value.value = '';
  }
}

function addButtonItem(kindParam) {
  if (settings.value.items.length >= maxItems) {
    toast.error(t('aiChatAgentSetup.webVoiceSettings.itemLimit', 'The visual library can contain up to 30 items.'));
    return;
  }
  const actionType = kindParam === 'purchase' ? 'purchase' : 'regular';
  const defaultName = actionType === 'purchase' ? t('aiChatAgentSetup.webVoiceSettings.purchaseButton', 'Purchase button') : t('aiChatAgentSetup.webVoiceSettings.regularButton', 'Regular button');
  const defaultLabel = actionType === 'purchase' ? t('aiChatAgentSetup.webVoiceSettings.purchaseLabelPlaceholder', 'Buy now') : t('aiChatAgentSetup.webVoiceSettings.regularLabelPlaceholder', 'Learn more');
  const items = settings.value.items.slice();
  items.push({
    id: createId(),
    itemKind: 'button',
    actionType,
    name: defaultName,
    type: actionType === 'purchase' ? 'action/purchase' : 'action/button',
    size: 0,
    dataUrl: '',
    prompt: '',
    actionLabel: defaultLabel,
    actionUrl: ''
  });
  isAddDropdownOpen.value = false;
  emitSettings(settings.value.enabled, items);
}

function updatePrompt(itemIdParam, promptParam) {
  const prompt = typeof promptParam === 'string' ? promptParam : '';
  const items = settings.value.items.map((item) => item.id === itemIdParam ? { ...item, prompt } : item);
  emitSettings(settings.value.enabled, items);
}

function updateItemField(itemIdParam, fieldParam, valueParam) {
  const allowedFields = ['name'];
  if (allowedFields.includes(fieldParam) !== true) {
    return;
  }
  const value = typeof valueParam === 'string' ? valueParam : '';
  const items = settings.value.items.map((item) => item.id === itemIdParam ? { ...item, [fieldParam]: value } : item);
  emitSettings(settings.value.enabled, items);
}

function updateActionLabel(itemIdParam, labelParam) {
  const actionLabel = typeof labelParam === 'string' ? labelParam : '';
  const items = settings.value.items.map((item) => item.id === itemIdParam ? { ...item, actionLabel } : item);
  emitSettings(settings.value.enabled, items);
}

function updateActionUrl(itemIdParam, urlParam) {
  const actionUrl = typeof urlParam === 'string' ? urlParam : '';
  const items = settings.value.items.map((item) => item.id === itemIdParam ? { ...item, actionUrl } : item);
  emitSettings(settings.value.enabled, items);
}

function requestDelete(itemParam) {
  deleteItem.value = itemParam;
}

function confirmDelete() {
  if (deleteItem.value === null) {
    return;
  }
  const itemId = deleteItem.value.id;
  emitSettings(settings.value.enabled, settings.value.items.filter((item) => item.id !== itemId));
  deleteItem.value = null;
}

function formatBytes(valueParam) {
  const bytes = Number(valueParam);
  if (Number.isFinite(bytes) !== true || bytes <= 0) {
    return '0 KB';
  }
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
</script>
